import React from 'react';
import 'react-is';
import ReactDOM from 'react-dom/client';
import './index.scss';
import reportWebVitals from './reportWebVitals';
import App from './App';
import { CookiesProvider } from 'react-cookie';
import { AdapterLuxon } from '@mui/x-date-pickers/AdapterLuxon';
import { LocalizationProvider } from '@mui/x-date-pickers';
import logger from './utils/logger';
import { ErrorLoggerProvider } from '@utils/LoggingProvider';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { LDProvider } from 'launchdarkly-react-client-sdk';
import { NotificationProvider } from './components';
import { Duration } from 'luxon';
import { createBrowserRouter, RouterProvider } from 'react-router-dom';
import { AdminHome, AuthError, ErrorPage, Groups, Home } from '@app/pages';
import { AdminJobs } from '@app/pages/admin/AdminJobs';
import { AdminPrizeDrawings } from '@app/pages/admin/AdminPrizeDrawings';
import { AdminQuestionnaires } from '@app/pages/admin/AdminQuestionnaires';
import { AdminQuestionnaireEditor } from '@app/pages/admin/AdminQuestionnaireEditor';
import { AdminQuestionnaireSchedules } from '@app/pages/admin/AdminQuestionnaireSchedules';
import { AdminQuestionnaireResults } from '@app/pages/admin/AdminQuestionnaireResults';
import { ActiveCohortUserGuard } from '@app/components/Guards/ActiveCohortUserGuard';
import { TeamChatScreen } from '@components/TeamChat';
import { DirectMessageScreen } from '@components/DirectMessage';
import { RoleGuard } from '@app/components/Guards/RoleGuard';
import { Role } from '@app/enums/Role';
import { Preferences, PreferencesLayout } from '@app/pages/Preferences';
import { ProfilePage } from '@app/pages/Profile/ProfilePage';
import { AcceptInvite } from '@app/pages/AcceptInvite';
import { NotRegistered } from '@app/pages/NotRegistered';
import { MagicLinkConfirm } from '@app/pages/MagicLinkConfirm';
import { DashboardWithCookies } from '@app/navigation/DashboardWithCookies';
import { PhonePage } from '@app/pages/Phone/PhonePage';
import { TermsGuard } from '@app/components/Guards/TermsGuard';
import { NameGuard } from '@app/components/Guards/NameGuard';
import { AnyActiveCohortUserGuard } from './components/Guards/AnyActiveCohortUserGuard';
import { AppCookiesProvider } from '@app/providers/AppCookieProvider';
import { DebugModeProvider } from '@app/providers/DebugModeProvider';
import { initAnalytics } from '@utils/analytics';

initAnalytics();

if ('serviceWorker' in navigator && 'PushManager' in window) {
  navigator.serviceWorker
    .register('/sw.js')
    .then((registration) => {
      logger.log('Service Worker registered with scope:', { scope: registration.scope });
    })
    .catch((error) => {
      logger.error('Service Worker registration failed:', { error });
    });
}

const router = createBrowserRouter([
  {
    path: '/',
    element: <App />, // Wrap everything in App
    errorElement: <ErrorPage />,
    children: [
      { index: true, element: <Home /> },
      {
        path: 'Dashboard',
        element: (
          <RoleGuard requiredRoles={[Role.Participant, Role.Captain, Role.Admin, Role.Coach]}>
            <TermsGuard>
              <NameGuard>
                <Groups />
              </NameGuard>
            </TermsGuard>
          </RoleGuard>
        ),
      },
      {
        path: 'Dashboard/:groupId',
        element: (
          <RoleGuard requiredRoles={[Role.Participant, Role.Captain, Role.Admin, Role.Coach]}>
            <TermsGuard>
              <NameGuard>
                <ActiveCohortUserGuard>
                  <DashboardWithCookies />
                </ActiveCohortUserGuard>
              </NameGuard>
            </TermsGuard>
          </RoleGuard>
        ),
      },
      {
        path: 'team-chat/:groupId',
        element: (
          <RoleGuard requiredRoles={[Role.Participant, Role.Captain, Role.Admin, Role.Coach]}>
            <TermsGuard>
              <NameGuard>
                <ActiveCohortUserGuard>
                  <NotificationProvider>
                    <TeamChatScreen />
                  </NotificationProvider>
                </ActiveCohortUserGuard>
              </NameGuard>
            </TermsGuard>
          </RoleGuard>
        ),
      },
      {
        path: 'direct-message/:groupId/:participantId',
        element: (
          <RoleGuard requiredRoles={[Role.Participant, Role.Captain, Role.Admin, Role.Coach]}>
            <TermsGuard>
              <NameGuard>
                <ActiveCohortUserGuard>
                  <NotificationProvider>
                    <DirectMessageScreen />
                  </NotificationProvider>
                </ActiveCohortUserGuard>
              </NameGuard>
            </TermsGuard>
          </RoleGuard>
        ),
      },
      {
        path: 'admin',
        element: (
          <RoleGuard requiredRoles={[Role.Admin, Role.Captain]}>
            <NameGuard>
              <AdminHome />
            </NameGuard>
          </RoleGuard>
        ),
      },
      {
        path: 'admin/jobs',
        element: (
          <RoleGuard requiredRoles={[Role.Admin]}>
            <NameGuard>
              <AdminJobs />
            </NameGuard>
          </RoleGuard>
        ),
      },
      {
        path: 'admin/questionnaires',
        element: (
          <RoleGuard requiredRoles={[Role.Admin]}>
            <NameGuard>
              <AdminQuestionnaires />
            </NameGuard>
          </RoleGuard>
        ),
      },
      {
        path: 'admin/questionnaires/schedules',
        element: (
          <RoleGuard requiredRoles={[Role.Admin]}>
            <NameGuard>
              <AdminQuestionnaireSchedules />
            </NameGuard>
          </RoleGuard>
        ),
      },
      {
        path: 'admin/questionnaires/results',
        element: (
          <RoleGuard requiredRoles={[Role.Admin]}>
            <NameGuard>
              <AdminQuestionnaireResults />
            </NameGuard>
          </RoleGuard>
        ),
      },
      {
        // :formId is "new" for a questionnaire not yet published.
        path: 'admin/questionnaires/:formId',
        element: (
          <RoleGuard requiredRoles={[Role.Admin]}>
            <NameGuard>
              <AdminQuestionnaireEditor />
            </NameGuard>
          </RoleGuard>
        ),
      },
      {
        path: 'admin/groups/:groupId/prize-drawings',
        element: (
          <RoleGuard requiredRoles={[Role.Admin]}>
            <NameGuard>
              <AdminPrizeDrawings />
            </NameGuard>
          </RoleGuard>
        ),
      },
      {
        path: 'preferences',
        element: (
          <RoleGuard requiredRoles={[Role.Participant, Role.Captain, Role.Admin, Role.Coach]}>
            <TermsGuard>
              <NameGuard>
                <PreferencesLayout />
              </NameGuard>
            </TermsGuard>
          </RoleGuard>
        ),
        children: [{ index: true, element: <Preferences /> }],
      },
      {
        path: 'validate-phone',
        element: (
          <RoleGuard requiredRoles={[Role.Participant, Role.Captain, Role.Admin, Role.Coach]}>
            <TermsGuard>
              <NameGuard>
                <PhonePage />
              </NameGuard>
            </TermsGuard>
          </RoleGuard>
        ),
        children: [{ index: true, element: <Preferences /> }],
      },
      {
        path: 'Teams',
        element: (
          <RoleGuard requiredRoles={[Role.Participant, Role.Captain, Role.Admin, Role.Coach]}>
            <TermsGuard>
              <NameGuard>
                <AnyActiveCohortUserGuard>
                  <Groups />
                </AnyActiveCohortUserGuard>
              </NameGuard>
            </TermsGuard>
          </RoleGuard>
        ),
      },
      {
        path: 'Profile',
        element: (
          <RoleGuard requiredRoles={[Role.Participant, Role.Captain, Role.Admin, Role.Coach]}>
            <TermsGuard>
              <NameGuard>
                <ProfilePage />
              </NameGuard>
            </TermsGuard>
          </RoleGuard>
        ),
      },
      { path: 'signin', element: <Home /> },
      { path: 'accept-invite', element: <AcceptInvite /> },
      { path: 'not-registered', element: <NotRegistered /> },
      { path: 'auth-error', element: <AuthError /> },
      { path: 'magic-link', element: <MagicLinkConfirm /> },
      { path: 'error', element: <ErrorPage /> },
      { path: '*', element: <ErrorPage /> },
    ],
  },
]);

const root = ReactDOM.createRoot(document.getElementById('root') as HTMLElement);

// Optimized QueryClient configuration with strategic caching
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Default stale time: 30 seconds for most data
      staleTime: Duration.fromObject({ seconds: 30 }).milliseconds,
      // Cache data for 5 minutes before garbage collection
      gcTime: Duration.fromObject({ minutes: 5 }).milliseconds,
      // Retry failed requests with exponential backoff
      retry: 2,
      retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
      // Refetch on window focus for fresh data
      refetchOnWindowFocus: true,
      // Don't refetch on mount if data is fresh
      refetchOnMount: false,
      // Refetch on reconnect to get latest data
      refetchOnReconnect: true,
    },
    mutations: {
      // Retry mutations once on failure
      retry: 1,
      retryDelay: 1000,
    },
  },
});
root.render(
  <React.StrictMode>
    <CookiesProvider>
      <AppCookiesProvider>
        <DebugModeProvider>
          <LocalizationProvider dateAdapter={AdapterLuxon}>
            <ErrorLoggerProvider>
              <QueryClientProvider client={queryClient}>
                <LDProvider clientSideID={import.meta.env.VITE_APP_LD_CLIENT_ID ?? '685179818b4e22093806d2fd'}>
                  <RouterProvider router={router} />
                </LDProvider>
              </QueryClientProvider>
            </ErrorLoggerProvider>
          </LocalizationProvider>
        </DebugModeProvider>
      </AppCookiesProvider>
    </CookiesProvider>
  </React.StrictMode>
);

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
